import { Entity } from '@backstage/catalog-model';
import { DefaultEntityFilters, EntityFilter, useEntityList } from '@backstage/plugin-catalog-react';
import { Select } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import { useEffect } from 'react';
import { MAINTAINED_BY_RELATION } from '../maintainers/constant.tsx';

// ── Filter class ─────────────────────────────────────────────────────────────

export type EntityMaintainedValue = 'true' | 'false';

export class EntityMaintainedFilter implements EntityFilter {
  constructor(readonly value: EntityMaintainedValue) {}

  filterEntity(entity: Entity): boolean {
    const isMaintained = (entity.relations ?? []).some(
      relation => relation.type === MAINTAINED_BY_RELATION,
    );
    return this.value === 'true' ? isMaintained : !isMaintained;
  }

  toQueryValue(): string {
    return this.value;
  }
}

// ── Extended filter bag ───────────────────────────────────────────────────────

export interface CustomMaintainedFilters extends DefaultEntityFilters {
  maintained?: EntityMaintainedFilter;
}

// ── Picker component ──────────────────────────────────────────────────────────

// Only relevant for entities that can carry a `maintainedBy` relation.
const VISIBLE_KINDS = ['component', 'resource'];

const ITEMS = [
  { value: 'all', label: 'all' },
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

export function EntityMaintainedPicker() {
  const { filters, updateFilters } = useEntityList<CustomMaintainedFilters>();

  const kind = filters.kind?.value?.toLocaleLowerCase('en-US') ?? '';
  const isVisible = VISIBLE_KINDS.includes(kind);

  const selected = filters.maintained?.value ?? 'all';

  useEffect(() => {
    if (!isVisible && filters.maintained) {
      updateFilters({ maintained: undefined });
    }
  }, [isVisible, filters.maintained, updateFilters]);

  if (!isVisible) return null;

  return (
    <Box pb={1} pt={1}>
      <Select
        label="Maintained"
        items={ITEMS}
        selected={selected}
        onChange={value =>
          updateFilters({
            maintained:
              value === 'all'
                ? undefined
                : new EntityMaintainedFilter(value as EntityMaintainedValue),
          })
        }
      />
    </Box>
  );
}
